import type { ComponentStyleConfig } from '@chakra-ui/theme';

/**
 * Style config for the CardDeal component.
 *
 * Parts: container | informationPanel | titleRow | descriptionRow |
 *        descriptionWrapper | descriptionText | nameText | detailText |
 *        metricsContainer | metric | metricLabel | metricValue |
 *        complianceText | tagsContainer | countryContainer | countryText |
 *        image
 *
 * Variants: eu | us
 *   - eu: shows the metrics row between the description and the tags
 *   - us: hides the metrics row
 */
// Viewport-driven font sizes, used by `metricValue` only: [mobile, desktop
// (sm), desktopPlus (md)]. The description block sizes off the card instead —
// see `descriptionTextSize` below.
const responsiveFontSize = { base: 'lg', sm: 'lg', md: 'md' };

// The description is clamped to a fixed number of lines so cards in a row stay
// aligned. Narrower cards fit fewer words per line, so they get one extra line.
// Driven by the card's own width via a container query — a viewport breakpoint
// can't tell a full-width mobile card from one of three in a desktop row.
const CARD_CONTAINER_NAME = 'card-deal';
const SMALL_CARD_MAX_WIDTH = '440px';
const SMALL_CARD_QUERY = `@container ${CARD_CONTAINER_NAME} (max-width: ${SMALL_CARD_MAX_WIDTH})`;
const DESCRIPTION_LINES_LARGE = 2;
const DESCRIPTION_LINES_SMALL = 3;
// Absolute rather than unitless: a fixed line box per card size, which keeps
// the height calc below independent of the rendered font-size.
const DESCRIPTION_LINE_HEIGHT = '1.5rem'; // 24px
const SMALL_CARD_DESCRIPTION_LINE_HEIGHT = '1.25rem'; // 20px

// The description block sizes off the card's own width too, on the same
// threshold as the line count: `lg` (18px) on a small card, `xl` (20px) on a
// regular one.
const descriptionTextSize = {
  fontSize: 'xl',
  lineHeight: DESCRIPTION_LINE_HEIGHT,
  [SMALL_CARD_QUERY]: {
    fontSize: 'md',
    lineHeight: SMALL_CARD_DESCRIPTION_LINE_HEIGHT,
  },
};

// Avatars size off the card's own width (48px / 40px below the small-card
// threshold, per the design) — a viewport breakpoint can't express this since
// the same screen holds both a 348px rail slide and a 690px grid card.
// These override the `Avatar` size token directly (tokens are JS-only, and
// the card's width isn't known there). Radii match `lg`/`xl`; `userAvatar`
// sets none, leaving the `round` variant in charge.
const AVATAR_BOX_SIZE = '48px';
const SMALL_CARD_AVATAR_BOX_SIZE = '40px';

// Everything the tags row's reserve (see `variants.us`) is derived from. They
// live here, next to the declarations that consume them, so the reserve and the
// pieces it measures can't drift apart.
/** A 20px `xs` Tag (`minH: 5`) plus the 4px gap to the row below it. */
const BADGE_ROW_HEIGHT = 24;
/** Badges wrap onto at most two rows; a third is clipped. */
const BADGE_ROWS_MAX = 2;
const COMPLIANCE_LINE_HEIGHT = 16;
// 6px rather than the design's 8px: `badgesWrapper` contributes the remaining
// 2px through its own `py`.
const TAGS_COLUMN_GAP = 6;
/** `tagsContainer`'s `pt`, part of its border-box height in the `us` variant. */
const TAGS_ROW_PADDING_TOP = 16;

/** Height of a tags column whose badges fit on one row, with no compliance line. */
const TAGS_ROW_HEIGHT = TAGS_ROW_PADDING_TOP + BADGE_ROW_HEIGHT;
/** The same, with the compliance line and its gap above the badges. */
const TAGS_ROW_HEIGHT_WITH_COMPLIANCE =
  TAGS_ROW_HEIGHT + COMPLIANCE_LINE_HEIGHT + TAGS_COLUMN_GAP;

export const styleConfig: ComponentStyleConfig = {
  parts: [
    'container',
    'informationPanel',
    'titleRow',
    'companyAvatar',
    'userAvatar',
    'descriptionRow',
    'descriptionWrapper',
    'descriptionText',
    'nameText',
    'detailText',
    'metricsContainer',
    'metric',
    'metricLabel',
    'metricValue',
    'complianceText',
    'tagsContainer',
    'tagsColumn',
    'badgesWrapper',
    'countryContainer',
    'countryText',
    'image',
  ],
  baseStyle: {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      overflow: 'hidden',
      borderRadius: 'xl',
      maxW: '690px',
      // Lets `descriptionText` query the card's own width instead of the
      // viewport's — the same card can be full-width on mobile and one of
      // three in a row on desktop.
      containerType: 'inline-size',
      containerName: CARD_CONTAINER_NAME,
      bg: 'neutral.fill-subtle',
      cursor: 'pointer',
      _hover: {
        bg: 'neutral.fill-active',
      },
      // `CardDeal` renders a `LinkOverlay` without Chakra's `LinkBox`, so it
      // has to replicate `LinkBox`'s elevation rule itself: the overlay's
      // `::before` forms a stacking context that would otherwise swallow
      // clicks on anything in-flow. Without this, interactive content in the
      // `badgesBlock` / `bookmarkSlot` slots is unreachable.
      'a[href]:not(.chakra-linkbox__overlay), button': {
        position: 'relative',
        zIndex: 1,
      },
    },
    informationPanel: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      p: 5,
      w: 'full',
      flexShrink: 0,
    },
    titleRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 2,
      pb: 4,
      w: 'full',
    },
    companyAvatar: {
      boxSize: AVATAR_BOX_SIZE,
      borderRadius: 'lg',
      img: { borderRadius: 'lg' },
      [SMALL_CARD_QUERY]: {
        boxSize: SMALL_CARD_AVATAR_BOX_SIZE,
        borderRadius: 'md',
        img: { borderRadius: 'md' },
      },
    },
    userAvatar: {
      boxSize: AVATAR_BOX_SIZE,
      [SMALL_CARD_QUERY]: { boxSize: SMALL_CARD_AVATAR_BOX_SIZE },
    },
    descriptionRow: {
      display: 'flex',
      alignItems: 'flex-start',
      w: 'full',
      mb: 6,
      overflow: 'hidden',
    },
    // The flex item and the line-clamp target must be different elements: a
    // flex container blockifies the specified `display` of its direct flex
    // items, which silently defeats the `-webkit-box`/`-webkit-line-clamp`
    // trick `noOfLines` relies on. So `flex`/`minW` (the flex-item side) live
    // here, and the clamp lives one level down on `descriptionText`.
    descriptionWrapper: {
      flex: 1,
      minW: 0,
    },
    // `fontSize` has to live here, not only on `nameText`/`detailText`, so the
    // clamped paragraph matches the spans it wraps.
    descriptionText: {
      ...descriptionTextSize,
      color: 'neutral.fg-primary',
      noOfLines: DESCRIPTION_LINES_LARGE,
      // Reserves the full block even when the text is shorter, so rows below it
      // align across a grid. `noOfLines` caps the maximum, so this is also the
      // effective height. Chakra compiles `noOfLines` to `--chakra-line-clamp`,
      // so overriding that variable below keeps clamp and height in step.
      minH: `calc(${DESCRIPTION_LINE_HEIGHT} * var(--chakra-line-clamp))`,
      [SMALL_CARD_QUERY]: {
        ...descriptionTextSize[SMALL_CARD_QUERY],
        noOfLines: DESCRIPTION_LINES_SMALL,
        minH: `calc(${SMALL_CARD_DESCRIPTION_LINE_HEIGHT} * var(--chakra-line-clamp))`,
      },
    },
    nameText: {
      ...descriptionTextSize,
      fontFamily: 'body',
      fontWeight: 'semibold',
      color: 'neutral.fg-primary',
      letterSpacing: 'less-tight',
    },
    detailText: {
      ...descriptionTextSize,
      fontFamily: 'body',
      fontWeight: 'normal',
      color: 'neutral.fg-secondary',
      letterSpacing: 'less-tight',
    },
    metricsContainer: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: 2,
      w: 'full',
      py: 3,
      borderBottom: '1px solid',
      borderColor: 'neutral.border-subtle',
    },
    metric: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      flex: 1,
      minW: 0,
    },
    metricLabel: {
      fontFamily: 'body',
      fontWeight: 'medium',
      color: 'neutral.fg-tertiary',
      fontSize: 'sm-md',
      lineHeight: '20px',
      letterSpacing: 'less-tight',
    },
    metricValue: {
      fontFamily: 'body',
      fontWeight: 'medium',
      color: 'neutral.fg-primary',
      lineHeight: '24px',
      letterSpacing: 'less-tight',
      fontSize: responsiveFontSize,
    },
    // Hosting entity · regulation, sitting above the tags. US-only, and gated
    // in the component rather than here — unlike `metricsContainer`, there is
    // no hidden-but-present node to reason about.
    complianceText: {
      fontFamily: 'body',
      fontWeight: 'normal',
      color: 'neutral.fg-tertiary',
      fontSize: 'sm',
      lineHeight: `${COMPLIANCE_LINE_HEIGHT}px`,
      letterSpacing: 'less-tight',
      // The design keeps this on one line, so a long entity name truncates
      // rather than pushing the tags row down and breaking grid alignment.
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxW: 'full',
    },
    // Aligns to `flex-end` so the country label stays level with the badges
    // rather than being pushed up by the taller column — which is what the
    // design draws, and what lets the `us` reserve below work.
    tagsContainer: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: 4,
      w: 'full',
      pt: `${TAGS_ROW_PADDING_TOP}px`,
    },
    // The compliance line stacked over the badges block.
    tagsColumn: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      flex: 1,
      minW: 0,
      gap: `${TAGS_COLUMN_GAP}px`,
    },
    badgesWrapper: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
      gap: 1,
      w: 'full',
      minW: 0,
      py: '0.5',
      // A floor as well as a cap: a card whose badge list renders nothing still
      // reserves one row, which is what makes the `us` reserve below exact
      // rather than only correct when an offering has badges. A third row is
      // clipped — it would overlap the description text.
      minH: `${BADGE_ROW_HEIGHT}px`,
      maxH: `${BADGE_ROWS_MAX * BADGE_ROW_HEIGHT}px`,
      overflow: 'hidden',
    },
    countryContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      flexShrink: 0,
    },
    countryText: {
      fontFamily: 'body',
      fontWeight: 'normal',
      color: 'neutral.fg-secondary',
      fontSize: 'md',
      lineHeight: '24px',
      letterSpacing: 'less-tight',
      whiteSpace: 'nowrap',
      textAlign: 'right',
    },
    image: {
      position: 'relative',
      w: 'full',
      flexShrink: 0,
      overflow: 'hidden',
      aspectRatio: '16 / 9',
    },
  },
  variants: {
    eu: {
      metricsContainer: {
        display: 'flex',
      },
    },
    us: {
      metricsContainer: {
        display: 'none',
      },
      /**
       * Keeps a wrapped second row of badges from pushing the hero image down.
       */
      tagsContainer: {
        overflow: 'visible',
        h: `${TAGS_ROW_HEIGHT}px`,
        // The compliance line is rendered conditionally, and it changes what a
        // single-row column measures — matched here rather than passed down as
        // a prop, so the reserve stays entirely in CSS.
        '&:has([data-compliance])': {
          h: `${TAGS_ROW_HEIGHT_WITH_COMPLIANCE}px`,
        },
      },
    },
  },
  defaultProps: {
    variant: 'eu',
  },
};
